'use client';

import Link from 'next/link';
import styles from './SectorMvpCards.module.scss';
import { gaClick, gaCta } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';

export interface SectorMvp {
  title: string;
  description: string;
  image?: string;
  ctaLabel?: string;
  ctaHref?: string;
}

export interface SectorMvpCardsProps {
  eyebrow?: string;
  title?: string;
  description?: string;
  mvps?: SectorMvp[];
  closingCta?: {
    title?: string;
    description?: string;
    label?: string;
    href?: string;
  };
}

export function SectorMvpCards({
  eyebrow = 'Idées de MVP',
  title = 'Simuler l’impact d’une première version',
  description = 'Simulez l’impact d’une première version mesurable avant de la construire.',
  mvps = [],
  closingCta,
}: SectorMvpCardsProps) {
  useAOS();

  if (mvps.length === 0) {
    return null;
  }

  return (
    <section className={styles.root}>
      <div className={styles.inner}>
        <div className={styles.header} data-aos="fade-up" data-aos-duration="700">
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          {title && <h2 className={styles.title}>{title}</h2>}
          {description && <p className={styles.description}>{description}</p>}
        </div>
        <div
          className={styles.grid}
          data-aos="fade-up"
          data-aos-duration="900"
          data-aos-delay="150"
        >
          {mvps.map((mvp, index) => {
            const { title, ctaLabel, ctaHref } = mvp;
            return (
              <article
                key={`${title}-${index}`}
                className={styles.card}
                onClick={() => gaClick('sector_mvp_card', { mvp_title: title })}
              >
                {mvp.image ? (
                  <div className={styles.imageWrapper}>
                    <img src={mvp.image} alt="" className={styles.image} loading="lazy" />
                    <div className={styles.overlay} aria-hidden="true" />
                  </div>
                ) : (
                  <div className={styles.textVisual} aria-hidden="true">
                    <span className={styles.textVisualIndex}>
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className={styles.textVisualLabel}>MVP</span>
                  </div>
                )}
                <div className={styles.content}>
                  <h3 className={styles.cardTitle}>{title}</h3>
                  <p className={styles.cardDescription}>{mvp.description}</p>
                  {ctaHref && ctaLabel && (
                    <Link
                      href={ctaHref}
                      className={styles.cta}
                      onClick={() => gaCta(ctaLabel, 'sector_mvp_card', ctaHref)}
                    >
                      {ctaLabel}
                      <span aria-hidden="true">→</span>
                    </Link>
                  )}
                </div>
              </article>
            );
          })}
        </div>
        {closingCta && closingCta.label && closingCta.href && (
          <div className={styles.closingCta} data-aos="fade-up" data-aos-duration="700">
            {closingCta.title && <h3 className={styles.closingTitle}>{closingCta.title}</h3>}
            {closingCta.description && (
              <p className={styles.closingDescription}>{closingCta.description}</p>
            )}
            <Link
              href={closingCta.href}
              className={styles.closingButton}
              onClick={() => gaCta(closingCta.label!, 'sector_mvp_closing_cta', closingCta.href!)}
            >
              {closingCta.label}
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
